import { supabase } from '@/integrations/supabase/client';
import { BASE_URL } from '@/lib/constants';

export type ReferralPlan = 'thrivemax' | 'thrivefamily';

class ReferralService {
  private readonly REFERRAL_CODE_KEY = 'referral_code';
  private readonly REFERRAL_CODE_EXPIRY_KEY = 'referral_code_expiry';
  private readonly SELECTED_PLAN_KEY = 'referral_selected_plan';
  private readonly EXPIRY_DAYS = 30;

  /**
   * Generate a referral link with the given code (default welcome page)
   */
  generateReferralLink(referralCode: string, targetPage: string = '/welcome'): string {
    return `${BASE_URL}${targetPage}?ref=${referralCode}`;
  }

  /**
   * Generate welcome page link for partners (main referral link)
   */
  generateWelcomeLink(referralCode: string): string {
    return `${BASE_URL}/welcome?ref=${referralCode}`;
  }

  /**
   * Extract referral code from URL or storage
   */
  extractReferralCode(): string | null {
    // First check URL params
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('ref');
    
    if (urlCode) {
      return urlCode;
    }

    // Then check localStorage (changed from sessionStorage for persistence across sessions)
    const storedCode = localStorage.getItem(this.REFERRAL_CODE_KEY);
    const expiryStr = localStorage.getItem(this.REFERRAL_CODE_EXPIRY_KEY);
    
    if (storedCode && expiryStr) {
      const expiry = parseInt(expiryStr);
      if (Date.now() < expiry) {
        return storedCode;
      } else {
        // Expired, clean up
        this.clearReferralCode();
      }
    }

    return null;
  }

  /**
   * Persist referral code to storage (uses localStorage for 30-day persistence)
   */
  persistReferralCode(code: string): void {
    const expiry = Date.now() + (this.EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    localStorage.setItem(this.REFERRAL_CODE_KEY, code);
    localStorage.setItem(this.REFERRAL_CODE_EXPIRY_KEY, expiry.toString());
    console.log('[Referral] Code persisted to localStorage:', code, 'expires:', new Date(expiry));
  }

  /**
   * Clear referral code from storage
   */
  clearReferralCode(): void {
    localStorage.removeItem(this.REFERRAL_CODE_KEY);
    localStorage.removeItem(this.REFERRAL_CODE_EXPIRY_KEY);
  }

  /**
   * Persist selected plan to storage
   */
  persistPlanSelection(plan: ReferralPlan): void {
    localStorage.setItem(this.SELECTED_PLAN_KEY, plan);
    console.log('[Referral] Plan selection persisted:', plan);
  }

  /**
   * Get selected plan from storage
   */
  getSelectedPlan(): ReferralPlan | null {
    const plan = localStorage.getItem(this.SELECTED_PLAN_KEY);
    if (plan === 'thrivemax' || plan === 'thrivefamily') {
      return plan;
    }
    return null;
  }

  /**
   * Clear selected plan from storage
   */
  clearPlanSelection(): void {
    localStorage.removeItem(this.SELECTED_PLAN_KEY);
  }

  /**
   * Track a referral event (conversion)
   */
  async trackReferralEvent(
    referralType: string,
    entityId: string,
    entityType: string,
    additionalMetadata?: Record<string, any>
  ): Promise<{ success: boolean; eventId?: string; error?: string }> {
    const code = this.extractReferralCode();
    
    if (!code) {
      console.log('[Referral] No referral code found, skipping tracking');
      return { success: false, error: 'No referral code' };
    }

    try {
      // Gather metadata
      const metadata = {
        utm_source: sessionStorage.getItem('utm_source'),
        utm_medium: sessionStorage.getItem('utm_medium'),
        utm_campaign: sessionStorage.getItem('utm_campaign'),
        utm_content: sessionStorage.getItem('utm_content'),
        utm_term: sessionStorage.getItem('utm_term'),
        page_path: window.location.pathname,
        referrer: document.referrer,
        ...additionalMetadata
      };

      console.log('[Referral] Tracking event:', {
        code,
        referralType,
        entityId,
        entityType,
        metadata
      });

      const { data, error } = await supabase.rpc('record_referral_event', {
        p_referral_code: code,
        p_referral_type_name: referralType,
        p_entity_id: entityId,
        p_entity_type: entityType,
        p_metadata: metadata
      });

      if (error) {
        console.error('[Referral] Error tracking event:', error);
        return { success: false, error: error.message };
      }

      console.log('[Referral] Event tracked successfully:', data);
      return { success: true, eventId: data };
    } catch (err) {
      console.error('[Referral] Exception tracking event:', err);
      return { success: false, error: String(err) };
    }
  }

  /**
   * Persist UTM parameters
   */
  persistUTMParams(): void {
    const params = new URLSearchParams(window.location.search);
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    
    utmParams.forEach(param => {
      const value = params.get(param);
      if (value) {
        sessionStorage.setItem(param, value);
      }
    });
  }

  /**
   * Get current referral source info (for affiliate dashboard)
   */
  async getReferralSourceInfo(): Promise<{
    id: string;
    name: string;
    email: string;
    role: string;
    referralCode: string;
    isActive: boolean;
  } | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('referral_sources')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error || !data) return null;

      return {
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role,
        referralCode: data.referral_code,
        isActive: data.is_active
      };
    } catch (err) {
      console.error('[Referral] Error fetching source info:', err);
      return null;
    }
  }
}

export const referralService = new ReferralService();
